import { useState, useRef, useEffect } from "react";

import { FormModal, InputField, SettingsList, SettingsListItem, Spinner, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { OrganizationSnippet } from "../../redux/reducers/organizationSnippet";
import { OrganizationSnippetCreateDialog } from "../agents/OrganizationSnippetCreateDialog";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";
import { ConfirmDialog } from "../ConfirmDialog";
import { IconButton } from "../base/IconButton";

type SnippetCardProps = {
  snippets: OrganizationSnippet[];
  isLoading: boolean;
  organizationId: string;
  onCreateSnippet: (snippet: OrganizationSnippet) => void;
  onUpdateSnippet: (id: string, params: { name: string }) => void;
  onDeleteSnippet: (id: string) => void | Promise<void>;
};

export const SnippetCard = ({ snippets, isLoading, organizationId, onCreateSnippet, onUpdateSnippet, onDeleteSnippet }: SnippetCardProps) => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState<OrganizationSnippet | null>(null);
  const [isDeletingSnippet, setIsDeletingSnippet] = useState(false);
  const [playingSnippetId, setPlayingSnippetId] = useState("");
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleEdit = (snippet: OrganizationSnippet) => {
    setEditingSnippet(snippet);
    setShowEditDialog(true);
  };

  const handleSaveEdit = (formData: { name: string }) => {
    if (editingSnippet && formData.name.trim()) {
      onUpdateSnippet(editingSnippet.id, { name: formData.name.trim() });
      setEditingSnippet(null);
    }
  };

  const handleDelete = (snippet: OrganizationSnippet) => {
    setEditingSnippet(snippet);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!editingSnippet || isDeletingSnippet) return;
    setIsDeletingSnippet(true);
    try {
      await onDeleteSnippet(editingSnippet.id);
      setShowDeleteDialog(false);
      setEditingSnippet(null);
    } finally {
      setIsDeletingSnippet(false);
    }
  };

  const handlePlay = (snippet: OrganizationSnippet) => {
    if (playingSnippetId === snippet.id) {
      // If already playing this snippet, pause it
      if (audioRef.current) {
        audioRef.current.pause();
        setPlayingSnippetId("");
      }
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Create new audio element and play
    audioRef.current = new Audio(snippet.data);
    setPlayingSnippetId(snippet.id);

    audioRef.current.play().catch(() => {
      setPlayingSnippetId("");
    });
  };

  // Handle audio end events
  useEffect(() => {
    if (!audioRef.current || !playingSnippetId) return;

    const audio = audioRef.current;
    const handleEnded = () => setPlayingSnippetId("");
    const handlePause = () => setPlayingSnippetId("");
    const handleError = () => setPlayingSnippetId("");

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("error", handleError);
    };
  }, [playingSnippetId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(date));
  };

  return (
    <OrganizationSettingsTab
      title="Audio Snippets"
      description="Pre-recorded audio files your agents can play during calls — hold music, disclaimers, or branded greetings."
      count={snippets.length}
    >
      {isLoading ? (
        <View className="flex items-center justify-center py-4">
          <Spinner className="!text-brand" />
        </View>
      ) : (
        <SettingsList>
          {snippets.map((snippet) => (
            <SettingsListItem
              key={snippet.id}
              title={snippet.name}
              subtitle={formatDate(snippet.createdAt)}
              icon="snippet"
            >
              <View className="flex items-center gap-2">
                {snippet.type === "audio" && snippet.data ? (
                  <button
                    onClick={() => handlePlay(snippet)}
                    disabled={!snippet.data}
                    className="flex flex-row items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
                  >
                    <Icon
                      name={playingSnippetId === snippet.id ? "pause" : "play"}
                      size={14}
                    />
                    {playingSnippetId === snippet.id ? "Pause" : "Play"}
                  </button>
                ) : snippet.type === "text" ? (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(snippet.data || "");
                    }}
                    disabled={!snippet.data}
                    className="flex flex-row items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
                  >
                    <Icon
                      name="copy"
                      size={14}
                    />
                    Copy
                  </button>
                ) : null}
                <IconButton
                  variant="ghost"
                  icon="edit"
                  onClick={() => handleEdit(snippet)}
                />
                <IconButton
                  variant="danger"
                  icon="trash"
                  onClick={() => handleDelete(snippet)}
                />
              </View>
            </SettingsListItem>
          ))}
          <button
            onClick={() => setShowCreateDialog(true)}
            className="flex flex-row items-center gap-2 px-4 py-3 w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
          >
            <View className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
              <Icon
                name="add"
                size={14}
                className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
              />
            </View>
            <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
              New snippet
            </Text>
          </button>
        </SettingsList>
      )}
      <OrganizationSnippetCreateDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        organizationId={organizationId}
        onCreated={onCreateSnippet}
      />
      {editingSnippet && (
        <FormModal
          isOpen={showEditDialog}
          onClose={() => {
            setShowEditDialog(false);
            setEditingSnippet(null);
          }}
          onSave={handleSaveEdit}
          config={{ name: editingSnippet.name }}
          title="Edit Snippet"
          saveLabel="Save Changes"
          className="w-full max-w-md min-w-0 max-h-[90vh] mx-4"
        >
          {({ localConfig, onChange }) => (
            <InputField
              label="Name"
              placeholder="Enter snippet name"
              value={localConfig.name}
              onChange={onChange("name")}
            />
          )}
        </FormModal>
      )}

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          if (isDeletingSnippet) return;
          setShowDeleteDialog(false);
          setEditingSnippet(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Snippet"
        message={`Are you sure you want to delete "${editingSnippet?.name}"?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={isDeletingSnippet}
        variant="danger"
      >
        <Text className="text-sm text-gray-600 dark:text-gray-400">This action cannot be undone. The snippet will be permanently removed.</Text>
      </ConfirmDialog>
    </OrganizationSettingsTab>
  );
};
